import { useEffect, useRef, useState } from 'react';

/** How long `active` has to last before the flag turns on. */
const delayMs = 300;
/** Once on, how long the flag stays on at least, so it never just blinks. */
const minOnMs = 400;

/**
 * Follows `active` with a grace delay: a short `true` never turns the flag on,
 * and once on it stays on for `minOnMs`. For a spinner that should stay quiet
 * on fast requests and not flash on the ones that end just after the delay.
 */
export function useDelayedFlag(active: boolean): boolean {
  const [on, setOn] = useState(false);
  const onSince = useRef(0);

  useEffect(() => {
    if (active && !on) {
      const timeout = setTimeout(() => {
        onSince.current = Date.now();
        setOn(true);
      }, delayMs);
      return () => clearTimeout(timeout);
    }
    if (!active && on) {
      const left = minOnMs - (Date.now() - onSince.current);
      const timeout = setTimeout(() => setOn(false), Math.max(0, left));
      return () => clearTimeout(timeout);
    }
  }, [active, on]);

  return on;
}
